import {
    Capacitor,
    PluginListenerHandle,
    registerPlugin,
} from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';

/*
 * In the phone app a minimized app cannot run script, so the end of the timer is handed to the
 * operating system as a clock-style alarm (android/.../TimerAlarmPlugin.java: AlarmManager.setAlarmClock,
 * rings on the alarm stream until dismissed, survives reboot; shows a lock-screen alarm screen or an actionable notification). It is cancelled the moment the timer is
 * paused, reset, skipped or finishes in the foreground, and cancelling also silences a ringing alarm.
 * The "running" notification below is a separate, silent one. Does nothing in a browser.
 */

export interface AlarmStatus {
    /** Notifications are on for the app. */
    notifications: boolean;
    /** The ring channel is still at high importance (the person can lower it in system settings). */
    channelOk: boolean;
    /** Android 14+ "Full screen notifications"; always true below 14. */
    fullScreen: boolean;
}
export type AlarmProblem = 'notifications' | 'channel' | 'fullScreen';

interface TimerAlarmPlugin {
    schedule(options: {
        endsAt: number;
        title: string;
        body: string;
        /** Bundled sound id, or 'system' for the device's alarm ringtone. */
        sound: string;
        /** Show a full-screen, clock-style alarm over the lock screen. */
        overlay: boolean;
        /** Label of the "start the next timer" button; empty when there is none. */
        actionLabel: string;
        dismissLabel: string;
        /** JSON of `NativeStep[]`: the timers the action button may start without the app ('' for none). */
        next: string;
        /** The timer being scheduled is a focus session. */
        completesFocus: boolean;
    }): Promise<void>;
    cancel(): Promise<void>;
    /** False when Android 14+ "Full screen notifications" is off for the app. */
    fullScreenAllowed(): Promise<{ allowed: boolean }>;
    openFullScreenSettings(): Promise<void>;
    /** What the alarm needs to reach the screen (see TimerAlarmPlugin.alarmStatus). */
    alarmStatus(): Promise<AlarmStatus>;
    openAlarmSettings(options: { kind: AlarmProblem }): Promise<void>;
    /** The button pressed on the alarm or the running notification (once). */
    consumeAction(): Promise<{
        action: string;
        endsAt?: number;
        mode?: string;
        focusDone?: number;
        completed?: number;
    }>;
    /** Foreground-service notification that counts down on its own and cannot be swiped away. */
    showRunning(options: {
        endsAt: number;
        title: string;
        body: string;
        cancelLabel: string;
    }): Promise<void>;
    hideRunning(): Promise<void>;
    addListener(
        eventName: 'timerCancelled',
        listener: () => void
    ): Promise<PluginListenerHandle>;
}
const TimerAlarm = registerPlugin<TimerAlarmPlugin>('TimerAlarm');

const available = () => Capacitor.isNativePlatform();

/** Asks for notification permission (Android 13+ and iOS), from a tap on Start. Resolves true if granted. */
export const ensureNativePermission = async (): Promise<boolean> => {
    if (!available()) return false;
    try {
        const current = await LocalNotifications.checkPermissions();
        if (current.display === 'granted') return true;
        if (current.display === 'denied') return false;
        return (
            (await LocalNotifications.requestPermissions()).display ===
            'granted'
        );
    } catch {
        return false;
    }
};

const FULLSCREEN_ASKED_KEY = 'tomato-pop:asked-fullscreen';

/**
 * Lock-screen alarms need Android's "Full screen notifications" switch. If it is off, opens its settings page:
 * once on its own (from Start), or every time when `force` (the person just turned the option on).
 */
export const ensureFullScreenAllowed = async (force = false): Promise<void> => {
    if (!available()) return;
    try {
        if (!force && localStorage.getItem(FULLSCREEN_ASKED_KEY)) return;
    } catch {
        // No storage: ask anyway.
    }
    try {
        if ((await TimerAlarm.fullScreenAllowed()).allowed) return;
        try {
            localStorage.setItem(FULLSCREEN_ASKED_KEY, 'true');
        } catch {
            // Ignored.
        }
        await TimerAlarm.openFullScreenSettings();
    } catch {
        // Older plugin build: nothing to ask.
    }
};

/** What stops the alarm from lighting the screen right now, or null when all is fine (or not the phone app). */
export const getAlarmProblem = async (): Promise<AlarmProblem | null> => {
    if (!available()) return null;
    try {
        const s = await TimerAlarm.alarmStatus();
        if (!s.notifications) return 'notifications';
        if (!s.channelOk) return 'channel';
        if (!s.fullScreen) return 'fullScreen';
        return null;
    } catch {
        // Older plugin build: nothing to report.
        return null;
    }
};

/** Opens the system page where `problem` can be fixed. */
export const openAlarmSettings = async (
    problem: AlarmProblem
): Promise<void> => {
    if (!available()) return;
    try {
        await TimerAlarm.openAlarmSettings({ kind: problem });
    } catch {
        // Ignored.
    }
};

/**
 * A timer the alarm's action button starts on its own, without the app (see AlarmScheduler.startNext). The
 * page works out the whole step, texts included, because the app may not be running when it is needed.
 */
export interface NativeStep {
    mode: string;
    ms: number;
    /** `focusDone` once this step is the current timer. */
    focusDone: number;
    /** This step is a focus session: finishing it is owed to the page's statistics. */
    endsFocus: boolean;
    /** The "timer is done" notification at the end of this step. */
    title: string;
    body: string;
    /** Label of the button that starts the step after this one; empty when there is none. */
    actionLabel: string;
    /** The running notification while this step counts down; `{time}` becomes the end time. */
    runningTitle: string;
    runningBody: string;
    cancelLabel: string;
}

/** What happened natively while the page was away: the timer now running and the focus sessions it missed. */
export interface NativeProgress {
    endsAt: number;
    mode: string;
    focusDone: number;
    completed: number;
}

export interface NativeAction {
    /** 'startNext', 'cancelTimer' or ''. */
    action: string;
    /** For 'startNext' done by the phone itself: the timer that is running now. */
    progress?: NativeProgress;
}

/** Schedules the "timer is done" notification for `endsAt` (epoch ms), replacing any earlier one. */
export const scheduleNativeAlarm = async (
    endsAt: number,
    options: {
        title: string;
        body: string;
        sound: string;
        overlay: boolean;
        actionLabel: string;
        dismissLabel: string;
        /** The timers the action button may start without the app, nearest first. */
        next?: NativeStep[];
        completesFocus?: boolean;
    }
): Promise<void> => {
    if (!available()) return;
    try {
        const { next, completesFocus, ...rest } = options;
        await TimerAlarm.schedule({
            endsAt,
            ...rest,
            next: next?.length ? JSON.stringify(next) : '',
            completesFocus: Boolean(completesFocus),
        });
    } catch (error) {
        console.warn(
            'Could not schedule the end-of-timer alert:',
            (error as Error)?.message
        );
    }
};

/** The button pressed on the alarm or the running notification, once. */
export const consumeNativeAction = async (): Promise<NativeAction> => {
    if (!available()) return { action: '' };
    try {
        const r = await TimerAlarm.consumeAction();
        if (r.action === 'startNext' && r.endsAt && r.mode)
            return {
                action: r.action,
                progress: {
                    endsAt: r.endsAt,
                    mode: r.mode,
                    focusDone: r.focusDone ?? 0,
                    completed: r.completed ?? 0,
                },
            };
        return { action: r.action };
    } catch {
        return { action: '' };
    }
};

export const cancelNativeAlarm = async (): Promise<void> => {
    if (!available()) return;
    try {
        await TimerAlarm.cancel();
    } catch {
        // Nothing scheduled, or the plugin is unavailable.
    }
};

/**
 * Shows (or refreshes) the "timer running" notification: the system counts down to `endsAt` live, it cannot be
 * swiped away, and its Cancel button is reported through `onRunningNotificationCancel` (or `consumeNativeAction`).
 */
export const showRunningNotification = async (
    endsAt: number,
    title: string,
    body: string,
    cancelLabel: string
): Promise<void> => {
    if (!available()) return;
    try {
        await TimerAlarm.showRunning({ endsAt, title, body, cancelLabel });
    } catch (error) {
        console.warn(
            'Could not show the running-timer notification:',
            (error as Error)?.message
        );
    }
};

export const hideRunningNotification = async (): Promise<void> => {
    if (!available()) return;
    try {
        await TimerAlarm.hideRunning();
    } catch {
        // Nothing shown, or the plugin is unavailable.
    }
};

/** Calls `onCancel` when the Cancel button of the running notification is tapped. Returns an unsubscribe. */
export const onRunningNotificationCancel = (
    onCancel: () => void
): (() => void) => {
    if (!available()) return () => undefined;
    const handle = TimerAlarm.addListener('timerCancelled', onCancel);
    return () => {
        void handle.then((h) => h.remove());
    };
};

// ---- Streak reminders ----

const STREAK_CHANNEL = 'streak';
/** Reminder ids live in this range; the timer's own notifications use others. */
const STREAK_ID_BASE = 1000;
const STREAK_ID_MAX = 1099;

export interface StreakNotice {
    at: Date;
    title: string;
    body: string;
}

/** Replaces every scheduled streak reminder with `notices` (an empty list just clears them). Phone app only. */
export const scheduleStreakReminders = async (
    notices: StreakNotice[]
): Promise<void> => {
    if (!available()) return;
    try {
        const { notifications } = await LocalNotifications.getPending();
        const stale = notifications.filter(
            (n) => n.id >= STREAK_ID_BASE && n.id <= STREAK_ID_MAX
        );
        if (stale.length)
            await LocalNotifications.cancel({ notifications: stale });
        if (!notices.length) return;
        if ((await LocalNotifications.checkPermissions()).display !== 'granted')
            return;
        await LocalNotifications.createChannel({
            id: STREAK_CHANNEL,
            name: 'Streak',
            importance: 3,
            vibration: true,
        });
        await LocalNotifications.schedule({
            notifications: notices
                .slice(0, STREAK_ID_MAX - STREAK_ID_BASE)
                .map((n, i) => ({
                    id: STREAK_ID_BASE + i,
                    title: n.title,
                    body: n.body,
                    channelId: STREAK_CHANNEL,
                    schedule: { at: n.at, allowWhileIdle: true },
                })),
        });
    } catch (error) {
        console.warn(
            'Could not schedule streak reminders:',
            (error as Error)?.message
        );
    }
};
